'use client';

import { useState, useRef, useCallback, useEffect } from 'react';
import { useTranslations } from 'next-intl';
import { trackEvent } from '@/lib/analytics/matomo';
import { SURVEY_QUESTIONS, REQUIRED_QUESTION_COUNT } from './survey-config';
import SurveyProgress from './SurveyProgress';
import SurveyQuestion from './SurveyQuestion';
import SurveyThankYou from './SurveyThankYou';

type AnswerMap = Record<string, string | string[] | number>;
type ConditionalMap = Record<string, string>;
type ErrorMap = Record<string, string>;
type Status = 'idle' | 'submitting' | 'success' | 'error';

export default function LandingSurvey() {
  const t = useTranslations('landing.survey');

  const [answers, setAnswers] = useState<AnswerMap>({});
  const [conditionals, setConditionals] = useState<ConditionalMap>({});
  const [email, setEmail] = useState('');
  const [errors, setErrors] = useState<ErrorMap>({});
  const [status, setStatus] = useState<Status>('idle');
  const [submitError, setSubmitError] = useState('');

  // Track which sliders have been interacted with (vs. just showing default)
  const [sliderInteractedSet, setSliderInteractedSet] = useState<Set<string>>(
    () => new Set(),
  );

  // Track survey start for Matomo (fire once)
  const hasStarted = useRef(false);

  // Count answered required questions
  const answeredCount = SURVEY_QUESTIONS.filter((q) => {
    if (!q.required) return false;
    const val = answers[q.id];
    if (val === undefined || val === '') return false;
    if (q.type === 'checkbox' && Array.isArray(val) && val.length === 0) return false;
    if (q.type === 'slider' && !sliderInteractedSet.has(q.id)) return false;
    return true;
  }).length;

  const handleChange = useCallback((questionId: string, value: string | string[] | number) => {
    // Track survey start on first interaction
    if (!hasStarted.current) {
      hasStarted.current = true;
      trackEvent('survey', 'start');
    }

    setAnswers((prev) => ({ ...prev, [questionId]: value }));
    // Clear error when user answers
    setErrors((prev) => {
      if (!prev[questionId]) return prev;
      const next = { ...prev };
      delete next[questionId];
      return next;
    });
  }, []);

  const handleConditionalChange = useCallback((questionId: string, value: string) => {
    setConditionals((prev) => ({ ...prev, [questionId]: value }));
  }, []);

  const handleSliderInteract = useCallback((questionId: string) => {
    setSliderInteractedSet((prev) => {
      if (prev.has(questionId)) return prev;
      const next = new Set(prev);
      next.add(questionId);
      return next;
    });
  }, []);

  // Abandon tracking: fire on unload if started but not submitted
  useEffect(() => {
    const handleAbandon = () => {
      if (hasStarted.current && status !== 'success') {
        trackEvent('survey', 'abandon');
      }
    };

    window.addEventListener('beforeunload', handleAbandon);
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'hidden') handleAbandon();
    });

    return () => {
      window.removeEventListener('beforeunload', handleAbandon);
    };
  }, [status]);

  const validate = (): boolean => {
    const newErrors: ErrorMap = {};

    for (const q of SURVEY_QUESTIONS) {
      if (!q.required) continue;
      const val = answers[q.id];

      if (val === undefined || val === '') {
        newErrors[q.id] = t('validation.required');
      } else if (q.type === 'checkbox' && Array.isArray(val) && val.length === 0) {
        newErrors[q.id] = t('validation.selectOne');
      } else if (q.type === 'slider' && !sliderInteractedSet.has(q.id)) {
        newErrors[q.id] = t('validation.moveSlider');
      }
    }

    // Validate email format if provided
    if (email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      newErrors.email = t('validation.invalidEmail');
    }

    setErrors(newErrors);

    // Scroll to first error
    if (Object.keys(newErrors).length > 0) {
      const firstErrorId = Object.keys(newErrors)[0];
      const el = document.getElementById(`question-${firstErrorId}`);
      el?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      return false;
    }

    return true;
  };

  const handleSubmit = async () => {
    if (!validate()) return;

    setStatus('submitting');
    setSubmitError('');

    // Build payload matching Zod schema
    const payload: Record<string, unknown> = {};
    for (const q of SURVEY_QUESTIONS) {
      payload[q.id] = answers[q.id];
    }

    // Conditional text fields
    if (answers.q2_biggest_worry === 'other_q2' && conditionals.q2_biggest_worry) {
      payload.q2_other_text = conditionals.q2_biggest_worry;
    }
    if (answers.q5_current_tracking === 'app' && conditionals.q5_current_tracking) {
      payload.q5_app_name = conditionals.q5_current_tracking;
    }

    if (email) payload.email = email;

    try {
      const res = await fetch('/api/survey', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });

      if (res.status === 201) {
        setStatus('success');
        trackEvent('survey', 'complete');
      } else if (res.status === 429) {
        setStatus('error');
        setSubmitError(t('submitErrors.rateLimit'));
      } else {
        const data = await res.json().catch(() => ({}));
        setStatus('error');
        setSubmitError(data.error || t('submitErrors.generic'));
      }
    } catch {
      setStatus('error');
      setSubmitError(t('submitErrors.network'));
    }
  };

  if (status === 'success') {
    return (
      <section id="survey" className="py-16 sm:py-24 px-4 sm:px-6 lg:px-8 bg-cream">
        <div className="max-w-2xl mx-auto">
          <SurveyThankYou emailProvided={!!email} />
        </div>
      </section>
    );
  }

  return (
    <section id="survey" className="py-16 sm:py-24 px-4 sm:px-6 lg:px-8 bg-cream">
      <div className="max-w-2xl mx-auto">
        {/* Header */}
        <div className="text-center mb-10">
          <h2 className="font-heading text-2xl sm:text-3xl font-semibold text-dark mb-3">
            {t('heading')}
          </h2>
          <p className="text-gray-600 max-w-lg mx-auto">
            {t('subheading')}
          </p>
        </div>

        {/* Progress bar */}
        <SurveyProgress answered={answeredCount} total={REQUIRED_QUESTION_COUNT} />

        {/* Questions */}
        <div className="space-y-8 mt-8">
          {SURVEY_QUESTIONS.map((q) => (
            <SurveyQuestion
              key={q.id}
              question={q}
              value={answers[q.id]}
              conditionalValue={conditionals[q.id]}
              error={errors[q.id]}
              sliderInteracted={sliderInteractedSet.has(q.id)}
              onChange={handleChange}
              onConditionalChange={handleConditionalChange}
              onSliderInteract={handleSliderInteract}
            />
          ))}

          {/* Email field (not numbered, separate from questions) */}
          <div className="pt-6 border-t border-gray-200" id="question-email">
            <label className="block text-base font-semibold text-dark mb-1">
              {t('email.label')}
            </label>
            <p className="text-sm text-gray-500 mb-3">{t('email.hint')}</p>
            <input
              type="email"
              value={email}
              onChange={(e) => {
                setEmail(e.target.value);
                setErrors((prev) => {
                  if (!prev.email) return prev;
                  const next = { ...prev };
                  delete next.email;
                  return next;
                });
              }}
              placeholder={t('email.placeholder')}
              className="w-full px-3 py-2 text-sm border border-gray-200 rounded-brand focus:outline-none focus:border-teal-500 focus:ring-1 focus:ring-teal-500"
            />
            {errors.email && (
              <p className="text-sm text-red-500 mt-1">{errors.email}</p>
            )}
          </div>
        </div>

        {/* Submit */}
        <div className="mt-10 text-center">
          {submitError && (
            <p className="text-sm text-red-500 mb-4">{submitError}</p>
          )}
          <button
            onClick={handleSubmit}
            disabled={status === 'submitting'}
            className="px-8 py-3 bg-teal-500 hover:bg-teal-600 active:bg-teal-700 disabled:opacity-50 disabled:cursor-not-allowed text-white font-heading font-semibold rounded-brand transition-colors"
          >
            {status === 'submitting' ? t('submitting') : t('submitButton')}
          </button>
        </div>
      </div>
    </section>
  );
}
